import { useTranslation } from 'react-i18next';
import { PromotionIncludedPrice } from '@koob/ui';
import { Lightning } from '../../../../components/Lightning';
import { useTripFilters } from '@koob/trip-designer';
import { formatCurrency, tripFactsheetUrl } from '../../../../utils';
import { useLanguage } from '../../../../hooks';

// `@koob/trip-designer`'s `TripHotelCard` takes labels and a price slot as
// props instead of translating itself — this bundles the app's `t()` calls
// once for every call site (ComposeMap, ComposeMapItinerary, ComposeList,
// the suggested-hotel modal) instead of repeating them.
export function useTripHotelCardProps() {
  const { t } = useTranslation('hotel');
  const { lang } = useLanguage();
  const tripFilters = useTripFilters();

  return {
    instantAvailabilityLabel: t('instantAvaibility'),
    getFactsheetHref: hotel =>
      tripFactsheetUrl(`/hotels/${hotel?.id}`, {
        locale: lang,
        tripId: tripFilters?.tripId,
      }),
    providedByLabel: t('providedBy'),
    hotelUnavailableLabel: t('tripDesigner:compose.hotelNotAvailable'),
    priceIncludedLabel: t('tripDesigner:compose.priceIncluded'),
    continueLabel: t('misc:continue'),
    selectDayLabel: t('tripDesigner:compose.selectDay'),
    minimumStayLabel: count => t('minimumStay', { count }),
    renderInstantAvailabilityBadge: () => (
      <Lightning popover={t('instantAvaibility')} absolute={true} />
    ),
    renderPrice: hotelData => {
      if (!hotelData?.price) return null;

      const hotelRoom = hotelData?.rooms
        ?.slice()
        .sort((a, b) => a.price - b.price)[0];

      return (
        <div className="mt-0.5 text-gray-500 text-sm">
          <div>{t('from')}</div>
          <PromotionIncludedPrice
            priceWithoutPromo={formatCurrency({
              amount: hotelData?.priceWithoutPromo,
              currency: hotelData?.currency,
            })}
            promotions={hotelData?.appliedPromotions}
          />
          <div className="flex flex-wrap gap-x-1">
            <span className="text-orange-600 font-medium">
              {formatCurrency({
                amount: hotelData?.price,
                currency: hotelData?.currency,
              })}
            </span>
            <span>{t('perNight')}</span>
          </div>

          <div className="text-blue-500 content-center mt-0.5">
            <div className="text-xs truncate">
              {hotelRoom?.name ?? hotelRoom?.displayName}
            </div>
          </div>
        </div>
      );
    },
  };
}
